import { defineStore } from 'pinia';
import Cookies from 'js-cookie';
import { mapRawResult } from '@/utils/handelRes';
import request from '@/utils/request';
import { ElMessage } from 'element-plus';
import { isArray } from 'lodash';
// 定义用户信息接口
interface UserInfo {
    id?: string | number;
    username: string;
    email?: string;
    avatar?: string;
    role?: number | string;
    [key: string]: any;
}

export const useUserStore = defineStore('user', {
    state: () => ({
        // 用户基本信息，不包含敏感数据
        userInfo: null as UserInfo | null,
        // 是否已登录
        isLoggedIn: false,
        // 用户资源
        userResource: {} as any,
    }),

    getters: {
        // 获取用户信息
        getUserInfo: state => state.userInfo,
        // 检查是否登录
        getIsLoggedIn: state => state.isLoggedIn,
        // 获取用户资源
        getUserResource: state => state.userResource,
        // 获取用户图片资源
        getUserImageResource: state => {
            if (isArray(state.userResource.items)) {
                const imageList = state.userResource.items.filter(
                    (item: any) => item.resourceType === 'image'
                );
                return imageList.map(mapRawResult);
            }
        },
        // 获取用户视频资源
        getUserVideoResource: state => {
            if (isArray(state.userResource.items)) {
                return state.userResource.items.filter(
                    (item: any) => item.resourceType === 'video'
                );
            }
        },
        // 获取用户PPT资源
        getUserPPTResource: state => {
            if (isArray(state.userResource.items)) {
                return state.userResource.items.filter((item: any) => item.resourceType === 'ppt');
            }
        },
    },

    actions: {
        // 设置用户信息和登录状态
        setUser(userData: any) {
            if (!userData) return;

            // 提取需要的用户信息
            this.userInfo = {
                id: userData.user.id,
                username: userData.user.username,
                email: userData.user.email,
                avatar: userData.user.avatar,
                role: userData.user.roleId,
                hospital: userData.user.hospital,
                professionTitle: userData.user.professionTitle,
                department: userData.user.department,
            };

            this.isLoggedIn = true;
            // 如果后端返回了token，将其存储在cookie中
            if (userData.tokenInfo) {
                this.setAuthToken(userData.tokenInfo);
            }
        },
        // 设置用户资源
        setUserResource(resource: any[]) {
            this.userResource = resource;
        },

        // 设置token到cookie
        setAuthToken(tokenInfo: any) {
            // 设置token有效期为7天，根据实际需求调整
            Cookies.set('token', tokenInfo.token, {
                expires: tokenInfo.expiresIn / (24 * 60 * 60 * 1000),
                secure: process.env.NODE_ENV === 'production', // 生产环境下只通过HTTPS发送
                sameSite: 'strict', // 防止CSRF攻击
            });
        },

        // 获取token
        getAuthToken() {
            return Cookies.get('token');
        },

        // 清除用户信息和token
        logout() {
            this.userInfo = null;
            this.isLoggedIn = false;
            this.userResource = {};
            Cookies.remove('token');
        },

        // 检查用户是否已登录（可用于路由守卫）
        async checkAuth() {
            const token = Cookies.get('token');
            const res = await request({
                url: '/api/auth/checkToken',
                method: 'get',
                headers: {
                    Authorization: token,
                },
            });
            // console.log(res);
            return res.data.result.valid;
            // return !!token && this.isLoggedIn;
        },

        //获取用户资源
        async asyncGetUserResource(
            page: number = 1,
            limit: number = 12,
            type = 'all',
            search = ''
        ) {
            try {
                const res: any = await request({
                    url: `/api/resources/user?type=${type}&search=${search}`,
                    method: 'get',
                    headers: {
                        Authorization: `Bearer ${Cookies.get('token')}`,
                    },
                });
                // console.log('res', res);
                if (res.code === 200 && res.message === '获取用户资源成功') {
                    const userResource = res.data.result.resources;
                    const userInfo = res.data.result.user;
                    if (userResource?.items && Array.isArray(userResource.items)) {
                        userResource.items.forEach((item: any) => {
                            item.user = userInfo;
                        });
                        this.userResource = userResource;
                        return {
                            userResource,
                            userInfo,
                        };
                    }
                }
                return null;
            } catch (error) {
                // console.error('获取用户资源失败:', error);
                ElMessage.error('获取用户资源失败');
                return null;
            }
        },

        // 获取收藏列表
        async getFavorites(type: string = 'all', query: string = '') {
            try {
                const res = await request({
                    url: '/api/favorites',
                    method: 'get',
                    params: { type, query },
                    headers: {
                        Authorization: `Bearer ${Cookies.get('token')}`,
                    },
                });
                return res.data.result;
            } catch (error) {
                console.error('获取收藏列表失败:', error);
                throw error;
            }
        },

        // 取消收藏
        async cancelFavorite(id: string | number) {
            try {
                const res = await request({
                    url: `/api/favorites/${id}`,
                    method: 'delete',
                    headers: {
                        Authorization: `Bearer ${Cookies.get('token')}`,
                    },
                });
                return res.data.result;
            } catch (error) {
                console.error('取消收藏失败:', error);
                throw error;
            }
        },

        //验证用户密码是否正确
        async verifyPassword(password: string) {
            try {
                const res = await request({
                    url: `/api/auth/verifyPassword`,
                    method: 'post',
                    data: { password },
                    headers: {
                        Authorization: `Bearer ${Cookies.get('token')}`,
                    },
                });
                return res;
            } catch (error) {
                console.error('验证密码失败:', error);
                throw error;
            }
        },

        //获取用户信息
        async fetchUserInfo(id: string | number) {
            try {
                const res: any = await request({
                    url: `/api/users/${id}`,
                    method: 'get',
                });
                // console.log('res', res);
                if (res.code === 200 && res.message === '查询用户成功') {
                    return res.data.result;
                } else {
                    ElMessage.error('获取用户信息失败。');
                }
                // return res.data.result;
            } catch (error) {}
        },
        // 更新用户资料
        async updateUserProfile(updateUserInfoForm: any) {
            try {
                // 创建一个新对象，只包含API所需的字段
                const apiData: Record<string, any> = {
                    username: updateUserInfoForm.username,
                    email: updateUserInfoForm.email,
                    gender: updateUserInfoForm.gender,
                    hospital: updateUserInfoForm.hospital,
                    professionalTitle: updateUserInfoForm.title, // 注意这里是title转换为professionalTitle
                    department: updateUserInfoForm.department,
                };

                // 过滤掉undefined值
                Object.keys(apiData).forEach(key => {
                    if (apiData[key] === undefined) {
                        delete apiData[key];
                    }
                });

                const res: any = await request({
                    url: `/api/users/${updateUserInfoForm.id}`,
                    method: 'put',
                    data: apiData,
                    headers: {
                        Authorization: `Bearer ${Cookies.get('token')}`,
                    },
                });

                return res;
            } catch (error) {
                console.error('更新用户资料失败:', error);
                throw error;
            }
        },
        // 获取用户资料
        async getUserProfile() {},
    },

    // 持久化配置
    persist: true,
});
